import { useState } from "react";
import extractIdFromGid from "helpers/extract-gid";

const nonPatioVariant = (productObj) => {
    // For example, Color and Size
    const productObjOptions = productObj?.options?.map((option) => option?.values[0]);

    /* 
    *  Find variant whose selectedOptions are all a part of productObjOptions
    *  E.g: If productObjOptions = [Red, Large], it will find the variant whose selectedOptions are [Red, Large]
    */
    const findVariant = productObj?.variants?.edges.find((variant) => {
        const selectedOptions = variant?.node?.selectedOptions.map((option) => option?.value);
        const isMatch = productObjOptions.every((option) => selectedOptions.includes(option));
        return isMatch;
    });

    if (!findVariant) return productObj?.variants?.edges[0];

    return findVariant;
};

const patioVariant = (productObj) => productObj?.variants?.edges
    .find((variant) => (variant?.node?.selectedOptions[0]?.value === "ProGuard" || variant?.node?.selectedOptions[0]?.value === "Supreme") && variant?.node?.selectedOptions[1]?.value === "Gray");

const useProduct = (productObj, presetVariantId, isPatio) => {
    const setInitialVariant = () => {
        if (!presetVariantId) {
            return isPatio
                ? patioVariant(productObj)
                : nonPatioVariant(productObj);
        }

        const presetVariant = productObj?.variants?.edges.find((variant) => +extractIdFromGid(variant?.node?.id) === presetVariantId);
        
        if (!presetVariant) {
            return isPatio
                ? patioVariant(productObj)
                : nonPatioVariant(productObj);
        }
        return presetVariant;
    };

    const [product, setProduct] = useState({
        ...productObj,
        currentVariant: setInitialVariant(), 
    });

    const getVariantById = (variantId) => product?.variants?.edges?.find((variant) => variant?.node?.id === variantId);

    const getVariantByOptions = (newOption) => {
        // Creates a new array of the current selected options, mixing the old values and the new one that was just selected
        const newSelectedOptions = [
            ...product.currentVariant.node.selectedOptions
                .filter((selectedOption) => selectedOption.name !== newOption.name)
                .map((selectedOption) => selectedOption.value),
            newOption.value];

        // Find variant whose selectedOptions are all a part of newSelectedOptions
        const newVariant = product.variants.edges
            .find((variant) => variant.node.selectedOptions
                .every((selectedOption) => newSelectedOptions.includes(selectedOption.value)));

        // Checking if the new variant was not found
        if (!newVariant) {
            /*
            * Returns first Variant found with the new option if a variant has already not been found
            * E.g: If it can't find a variant with Fabric: Proguard (new value) and Color: Red (old value), it will find the first variant with the Fabric ProGuard
            * This is a case where Color is dependent upon the Fabric, and not every ProGuard Variant also has a red color
            */ 

            const firstVariantWithNewOption = product.variants.edges
                .find((variant) => variant.node.selectedOptions
                    .find((selectedOption) => selectedOption.value === newOption.value));

            if (firstVariantWithNewOption) return firstVariantWithNewOption;

            if (!firstVariantWithNewOption) return product.variants.edges[0];
        }

        return newVariant;
    };

    const getVariantImageIndex = (variant) => product?.images?.edges?.findIndex((image) => image?.node?.id === variant?.node?.image?.id);
    const getVariantImageIndexByUrl = (variant) => product?.images?.edges?.findIndex((image) => image?.node?.url === variant?.node?.image?.url);

    const setVariant = (variant) => {
        if (!variant) return;
        setProduct((prevState) => ({ ...prevState, currentVariant: variant }));
    };

    return {
        product, setProduct, getVariantById, getVariantImageIndex, setVariant, getVariantByOptions, getVariantImageIndexByUrl,
    };
};

export default useProduct;
